import { Eye, EyeOff, SearchX } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import { routes } from '~/app/routes';
import { FieldLabel, RichTextField, Select, TextField } from '~/components/form';
import { mlsErrorText } from '~/components/pages/mls/components/feedActions';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardSection,
  EmptyState,
  FormActions,
  LoadError,
  SkeletonFields,
} from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import type { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { isNotFound } from '~/lib/apiError';
import {
  attributionPlaceholders,
  CODE_OPTIONS,
  changedFields,
  DEFAULT_ATTRIBUTION_PREVIEW,
  type MlsConfigDraft,
  mlsConfigSchema,
  toCreateBody,
  toDraft,
} from '~/lib/mlsConfigForm';
import { queryErrorMessage } from '~/modules/query';
import {
  type MlsConfig,
  useCreateMlsConfigMutation,
  useUpdateMlsConfigMutation,
} from '~/modules/query/mls';

/** The widest MLS ID the realistiq `MLS.MLSID` column holds. */
const MLSID_LIMIT = 32;

type DraftState = Pick<
  ReturnType<typeof useRecordDraft<MlsConfigDraft, MlsConfigDraft>>,
  'draft' | 'patch' | 'dirty' | 'markSaved'
>;

interface MlsConfigTabProps {
  /** The feed's `mlsid`, which is the realistiq `MLSID`. Empty when the feed has none. */
  mlsid: string;
  config: {
    row?: MlsConfig;
    /** The row does not exist yet (a 404). */
    absent: boolean;
    error?: unknown;
    refetch: () => PromiseLike<{ data?: MlsConfig }>;
  };
  /** Owned by the page, so a draft survives a switch to another tab. */
  form: DraftState;
  /** The feed logo section, shown first; its upload is separate from this form's Save. */
  logo: React.ReactNode;
}

const idOf = (field: string) => `mls-config-${field}`;

const PATH_FIELDS = [
  {
    field: 'imagePath',
    label: 'Images absolute path',
    help: 'Where the feed writes listing images on the server.',
  },
  {
    field: 'imageUrl',
    label: 'Images URL',
    help: 'The public address those images are served from.',
  },
  {
    field: 'attachmentPath',
    label: 'Attachments absolute path',
    help: 'Where the feed writes listing attachments on the server.',
  },
] as const;

/** The feed's realistiq MLS row: create, edit and the display of listing attribution (PRD requirements 1 to 15). */
export default function MlsConfigTab({ mlsid, config, form, logo }: MlsConfigTabProps) {
  const { draft, patch, dirty, markSaved } = form;
  const [updateConfig] = useUpdateMlsConfigMutation();
  const [createConfig] = useCreateMlsConfigMutation();
  const [created, setCreated] = useState<MlsConfig | null>(null);
  const [conflict, setConflict] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const picker = useRef<{ insertText: (text: string) => void }>(null);

  const stored = config.row ?? created ?? undefined;
  const editing = stored !== undefined;
  const loaded = useMemo(() => (stored ? toDraft(stored) : null), [stored]);
  const locked = stored?.pathsLocked;
  const v = useFormValidation(mlsConfigSchema, draft, idOf);

  const save = useRecordSave<MlsConfigDraft>({
    save: async (submitted) => {
      try {
        if (loaded) {
          const updated = await updateConfig({
            mlsid,
            data: changedFields(loaded, submitted),
          }).unwrap();
          markSaved(submitted, toDraft(updated));
        } else {
          const made = await createConfig(toCreateBody(mlsid, submitted)).unwrap();
          setCreated(made);
          setConflict(false);
          markSaved(submitted, toDraft(made));
        }
      } catch (err) {
        const status = (err as { status?: number } | undefined)?.status;
        if (!loaded && status === 409) setConflict(true);
        if (loaded && isNotFound(err)) {
          // The row was removed meanwhile: the retry below lands on the create form with the draft intact.
          void config.refetch();
          throw {
            detail: 'This MLS config no longer exists. Create it again with the values below.',
          };
        }
        throw { detail: mlsErrorText(err as { data?: { result?: unknown } }) };
      }
    },
    success: loaded ? 'MLS config updated' : 'MLS config created',
    failure: 'The MLS config could not be saved. Try again.',
  });

  const reload = async () => {
    const result = await config.refetch();
    if (result.data && draft) markSaved(draft, toDraft(result.data));
    setConflict(false);
    save.clearError();
  };

  if (!mlsid) {
    return (
      <Card>
        <CardBody>
          <EmptyState
            icon={<SearchX className="size-8" aria-hidden="true" />}
            title="An MLS ID is needed"
            description="A feed's MLS config is linked to it by its MLS ID. Add the MLS ID to the feed's configuration first."
            action={
              <Button
                href="?tab=configuration"
                variant="outline"
                size="sm"
                testId="mls-config-needs-id-link"
              >
                Open the Configuration tab
              </Button>
            }
          />
        </CardBody>
      </Card>
    );
  }

  // The logo upload does not depend on the MLS row, so it stays usable while the row can't be shown.
  const withLogo = (body: React.ReactNode) => (
    <Card>
      <CardBody className="space-y-6">
        <CardSection title="Feed">
          <FieldLabel>Logo</FieldLabel>
          {logo}
        </CardSection>
        <CardSection title="MLS config">{body}</CardSection>
      </CardBody>
    </Card>
  );

  if (config.error && !config.absent && !config.row) {
    return withLogo(
      <LoadError
        what="the MLS config"
        message={queryErrorMessage(config.error) ?? 'Try again in a moment.'}
        onRetry={() => void config.refetch()}
        testId="mls-config-load-error"
      />,
    );
  }

  if (config.absent && !stored && mlsid.length > MLSID_LIMIT) {
    return withLogo(
      <EmptyState
        icon={<SearchX className="size-8" aria-hidden="true" />}
        title="This MLS ID is too long"
        description={`An MLS config can't be created because its MLS ID is longer than ${MLSID_LIMIT} characters. Shorten the MLS ID on the Configuration tab.`}
      />,
    );
  }

  if (!draft) {
    return withLogo(<SkeletonFields fields={6} />);
  }

  const text = (
    field: 'description' | 'contact' | 'phone' | 'userName' | 'feedUrl',
    label: string,
    limit: number,
    helperText?: string,
  ) => (
    <TextField
      id={idOf(field)}
      testId={idOf(field)}
      label={label}
      value={draft[field]}
      onChange={(value) => patch({ [field]: value })}
      onBlur={() => v.touch(field)}
      error={v.errors[field]}
      maxLength={limit}
      helperText={helperText}
    />
  );

  const code = (field: keyof typeof CODE_OPTIONS, label: string, helperText?: string) => (
    <Select
      id={idOf(field)}
      testId={idOf(field)}
      label={label}
      placeholder="Not set"
      options={CODE_OPTIONS[field]}
      value={draft[field]}
      onChange={(value) => patch({ [field]: value })}
      helperText={helperText}
    />
  );

  const custom = draft.listingAttrType === 'CUSTOM';

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!v.submit()) return;
    await save.submit(draft);
  };

  return (
    <Card>
      <form id="mls-config-form" noValidate onSubmit={submit}>
        <CardBody className="space-y-6">
          {save.error && (
            <Alert variant="error">
              <p className="text-sm text-gray-800 dark:text-white/90">{save.error}</p>
              {conflict && (
                <Button
                  variant="link"
                  onClick={() => void reload()}
                  className="mt-1"
                  testId="mls-config-reload"
                >
                  Reload the MLS config
                </Button>
              )}
            </Alert>
          )}
          {created && (
            <Alert
              variant="warning"
              title="Upload the feed logo"
              message="The MLS config is created. Upload the feed's logo under Feed below so listings can show it."
            />
          )}

          <CardSection title="Feed">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div>
                <FieldLabel>Logo</FieldLabel>
                {logo}
              </div>
              {code('feedType', 'Feed type')}
            </div>
          </CardSection>

          <CardSection
            title="Details"
            description={
              editing
                ? "The feed's contact details and vendor connection."
                : 'No MLS config exists for this feed yet. Fill in the details and create it.'
            }
          >
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <TextField
                id={idOf('mlsid')}
                testId={idOf('mlsid')}
                label="MLS ID"
                value={mlsid}
                onChange={() => {}}
                readOnly
                helperText="Set on the feed's Configuration tab."
              />
              {text('description', 'Description', 64)}
              {text('contact', 'Contact', 64)}
              {text('phone', 'Phone', 64)}
              {text('userName', 'User name', 64)}
              <TextField
                id={idOf('password')}
                testId={idOf('password')}
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="off"
                value={draft.password}
                onChange={(value) => patch({ password: value })}
                onBlur={() => v.touch('password')}
                error={v.errors.password}
                maxLength={128}
                trailingActions={
                  <Button
                    variant="ghost"
                    size="icon"
                    ariaLabel={showPassword ? 'Hide password' : 'Show password'}
                    title={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((shown) => !shown)}
                    testId="mls-config-password-toggle"
                    startIcon={
                      showPassword ? (
                        <EyeOff className="size-4" aria-hidden="true" />
                      ) : (
                        <Eye className="size-4" aria-hidden="true" />
                      )
                    }
                  />
                }
              />
              {text('feedUrl', 'Feed URL', 128)}
              <TextField
                id={idOf('rowLimit')}
                testId={idOf('rowLimit')}
                label="Row limit"
                inputMode="numeric"
                value={draft.rowLimit}
                onChange={(value) => patch({ rowLimit: value })}
                onBlur={() => v.touch('rowLimit')}
                error={v.errors.rowLimit}
                helperText="A whole number from 0 to 999999."
              />
              {code('listTracEnabled', 'ListTrac enabled')}
              {code('showOffMarketListings', 'Show off-market listings')}
            </div>
          </CardSection>

          <CardSection title="Paths" description="Where the feed's images and attachments live.">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {PATH_FIELDS.map(({ field, label, help }) =>
                locked?.[field] ? (
                  <TextField
                    key={field}
                    id={idOf(field)}
                    testId={idOf(field)}
                    label={label}
                    value=""
                    onChange={() => {}}
                    readOnly
                    placeholder="Stored in a format Toolbox can't edit"
                    helperText="This value is shown read-only and is saved unchanged."
                  />
                ) : (
                  <TextField
                    key={field}
                    id={idOf(field)}
                    testId={idOf(field)}
                    label={label}
                    value={draft[field]}
                    onChange={(value) => patch({ [field]: value })}
                    onBlur={() => v.touch(field)}
                    error={v.errors[field]}
                    helperText={help}
                  />
                ),
              )}
            </div>
          </CardSection>

          <CardSection
            title="Listing display"
            description="The disclaimer and attribution shown with each listing."
          >
            <div className="space-y-6">
              <RichTextField
                id={idOf('disclaimer')}
                testId={idOf('disclaimer')}
                label="Disclaimer"
                value={draft.disclaimer}
                onChange={(html) => patch({ disclaimer: html })}
                helperText="Shown with listings. Characters outside Windows-1252, such as emoji, can't be saved."
              />
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {code('listingAttrLocation', 'Attribution location')}
                {code('listingAttrType', 'Attribution fields')}
              </div>
              {custom ? (
                <div className="space-y-4">
                  <div className="lg:w-1/2">
                    <Select
                      id={idOf('attribution-picker')}
                      testId={idOf('attribution-picker')}
                      label="Insert field"
                      placeholder="Choose a field to insert"
                      options={attributionPlaceholders(mlsid)}
                      value=""
                      onChange={(value) => picker.current?.insertText(value)}
                      helperText="Adds the field where the cursor is."
                    />
                  </div>
                  <RichTextField
                    id={idOf('listingAttrContent')}
                    testId={idOf('listingAttrContent')}
                    label="Custom attribution"
                    value={draft.listingAttrContent}
                    onChange={(html) => patch({ listingAttrContent: html })}
                    insertRef={picker}
                  />
                </div>
              ) : (
                <div data-testid="mls-config-attribution-preview">
                  <FieldLabel>Attribution preview</FieldLabel>
                  <div className="space-y-1 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-300">
                    {DEFAULT_ATTRIBUTION_PREVIEW.map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </div>
                  <p className="field-helper">
                    A sample of the default attribution. Choose Custom to write your own.
                  </p>
                </div>
              )}
            </div>
          </CardSection>
        </CardBody>
        <CardFooter>
          <FormActions
            form="mls-config-form"
            saveLabel={editing ? 'Save changes' : 'Create MLS config'}
            busyLabel={editing ? 'Saving…' : 'Creating…'}
            discardHref={routes.feeds}
            busy={save.busy}
            disabled={editing && !dirty}
            disabledReason="No changes to save"
            saveTestId="mls-config-save"
            discardTestId="mls-config-cancel"
          />
        </CardFooter>
      </form>
    </Card>
  );
}
